// ✅ src/pages/SMS/TestSendSMS.jsx  (FULL FILE)
// UI matches your screenshot: "Send SMS" page
// Fields: Mobile*, Message, Send button
//
// ✅ Backend expected (adjust API_BASE if your mount differs):
//   POST http://nissi-india.com/api/sendSMS/send
//   body: {
//     mobile: "9003744644",
//     message: "....",
//     // optional (if your backend needs these like ASPX session values):
//     supId, branchId, invAmt, grnNum, loginUserId
//   }
//
// ✅ Prefill message (same as ASPX):
// "Debited Amount:<Invamt>,Cheque No:,Cheque Date:,Branch:<branch> Payment Successfully,SRC Projects Pvt Ltd on <dd-MM-yyyy hh:mm tt>"
//
// Where values are read from (best effort):
// 1) location.state (recommended)
// 2) URL query (?mobile=&invAmt=&branch=&supId=&branchId=&grnNum=&loginUserId=)
// 3) sessionStorage keys (optional): mobno, Invamt, branch, supid, brid, grnnum, loginuserid

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/sendSMS";

const toStr = (v) => String(v ?? "").trim();

// dd-MM-yyyy hh:mm tt (like your ASPX)
const formatNow = () => {
  const d = new Date();
  const pad2 = (n) => String(n).padStart(2, "0");
  const dd = pad2(d.getDate());
  const mm = pad2(d.getMonth() + 1);
  const yyyy = d.getFullYear();

  let hh = d.getHours();
  const tt = hh >= 12 ? "PM" : "AM";
  hh = hh % 12;
  if (hh === 0) hh = 12;

  const min = pad2(d.getMinutes());
  return `${dd}-${mm}-${yyyy} ${pad2(hh)}:${min} ${tt}`;
};

const readQuery = (search) => {
  const p = new URLSearchParams(search || "");
  const get = (k) => {
    const v = p.get(k);
    return v == null ? "" : v;
  };
  return {
    mobile: get("mobile"),
    invAmt: get("invAmt"),
    branch: get("branch"),
    supId: get("supId"),
    branchId: get("branchId"),
    grnNum: get("grnNum"),
    loginUserId: get("loginUserId"),
  };
};

const TestSendSMS = () => {
  const { skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();
  const location = useLocation();

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);

  // ---- Source values (like Session in ASPX) ----
  const stateVals = location.state || {};
  const queryVals = useMemo(() => readQuery(location.search), [location.search]);

  // fallback to sessionStorage (optional)
  const storageVals = useMemo(
    () => ({
      mobile: sessionStorage.getItem("mobno") || "",
      invAmt: sessionStorage.getItem("Invamt") || "",
      branch: sessionStorage.getItem("branch") || "",
      supId: sessionStorage.getItem("supid") || "",
      branchId: sessionStorage.getItem("brid") || "",
      grnNum: sessionStorage.getItem("grnnum") || "",
      loginUserId: sessionStorage.getItem("loginuserid") || "",
    }),
    []
  );

  const resolved = useMemo(() => {
    const pick = (k) =>
      toStr(stateVals?.[k]) || toStr(queryVals?.[k]) || toStr(storageVals?.[k]) || "";
    return {
      mobile: pick("mobile"),
      invAmt: pick("invAmt"),
      branch: pick("branch"),
      supId: pick("supId"),
      branchId: pick("branchId"),
      grnNum: pick("grnNum"),
      loginUserId: pick("loginUserId"),
    };
  }, [stateVals, queryVals, storageVals]);

  const [mobile, setMobile] = useState(resolved.mobile);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Prefill message on first load (like !IsPostBack)
  useEffect(() => {
    const invAmt = resolved.invAmt || "";
    const branch = resolved.branch || "";
    setMessage(
      `Debited Amount:${invAmt},Cheque No:,Cheque Date:,Branch:${branch} Payment Successfully,SRC Projects Pvt Ltd on ${formatNow()}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  const validateMobile = (v) => {
    const s = toStr(v).replace(/\s+/g, "");
    // allow 10-13 digits (India +91 or without)
    return /^[0-9]{10,13}$/.test(s);
  };

  const onSend = async () => {
    const mob = toStr(mobile).replace(/\s+/g, "");
    const msg = toStr(message);

    if (!mob) {
      toast.error("Please enter mobile number");
      return;
    }
    if (!validateMobile(mob)) {
      toast.error("Enter valid mobile number (10-13 digits)");
      return;
    }
    if (!msg) {
      toast.error("Please enter message");
      return;
    }

    setSending(true);
    try {
      const payload = {
        mobile: mob,
        message: msg,

        // optional (your backend can ignore if not needed)
        supId: resolved.supId || undefined,
        branchId: resolved.branchId || undefined,
        invAmt: resolved.invAmt || undefined,
        grnNum: resolved.grnNum || undefined,
        loginUserId: resolved.loginUserId || undefined,
      };

      const { data } = await axios.post(`${API_BASE}/send`, payload);

      if (data?.success) {
        toast.success(data?.message || "Message Sent");

        // clear like ASPX
        setMobile("");
        setMessage("");

        // mimic window.close(); (works only if opened by script)
        setTimeout(() => {
          try {
            window.close();
          } catch (e) {}
          // fallback: go back
          navigate(-1);
        }, 500);
      } else {
        toast.error(data?.message || "Message Sending Failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Message Sending Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <HeaderBar />
      <VehicleLayoutWrapper>
        <div className="p-3">
          <ToastContainer />

          {/* Title bar like ASPX */}
          <div className="mb-2 text-blue-700 font-semibold">Send Mail</div>

          {/* Form area (simple table layout like screenshot) */}
          <div className="border rounded bg-white">
            <div className="p-3">
              <div className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-2 text-sm font-semibold">
                  Mobile <span className="text-red-600">*</span>
                </div>
                <div className="col-span-1 text-center">:</div>
                <div className="col-span-9">
                  <input
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="Enter mobile number"
                    disabled={sending}
                  />
                </div>

                <div className="col-span-2 text-sm font-semibold">Message</div>
                <div className="col-span-1 text-center">:</div>
                <div className="col-span-9">
                  <textarea
                    className="w-full border rounded px-2 py-1 text-sm"
                    rows={2}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={sending}
                  />
                </div>

                <div className="col-span-2" />
                <div className="col-span-1" />
                <div className="col-span-9">
                  <button
                    type="button"
                    onClick={onSend}
                    disabled={sending}
                    className={`${buttonClass} text-white px-4 py-2 rounded shadow hover:opacity-95 disabled:opacity-60`}
                    title="Send"
                  >
                    {sending ? "Sending..." : "Send"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* tiny help line */}
          <div className="text-xs text-gray-500 mt-2">
            Note: Mobile accepts 10-13 digits. Message is prefilled like your ASPX session logic.
          </div>
        </div>
      </VehicleLayoutWrapper>
    </>
  );
};

export default TestSendSMS;
